import React, { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { FaSearch } from "react-icons/fa";
import { cx } from "../../../style";
import "./CommandPalette.css";

const MAX_RESULTS = 50;

/**
 * CommandPalette — глобальный поиск по вкладкам (Ctrl/⌘ + K).
 *
 * @param {Array<{ name: string, path: string, group?: string }>} items  Индекс переходов.
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(path: string) => void} onNavigate  Переход по выбранной вкладке.
 */
export default function CommandPalette({ items = [], open, onClose, onNavigate }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ["name", "group"],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [items],
  );

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return items.slice(0, MAX_RESULTS);
    return fuse.search(q).map((r) => r.item).slice(0, MAX_RESULTS);
  }, [query, items, fuse]);

  // Сброс и фокус при открытии
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => setActive(0), [query]);

  // Подскроллить активный пункт
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, results]);

  if (!open) return null;

  const choose = (item) => {
    if (!item) return;
    onNavigate(item.path);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="ui-cmdk__backdrop" onMouseDown={onClose}>
      <div
        className="ui-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Поиск по вкладкам"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ui-cmdk__search">
          <span className="ui-cmdk__search-icon">
            <FaSearch size={16} />
          </span>
          <input
            ref={inputRef}
            className="ui-cmdk__input"
            placeholder="Поиск по вкладкам…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="ui-cmdk__esc">Esc</kbd>
        </div>

        <div className="ui-cmdk__list" ref={listRef}>
          {results.length === 0 ? (
            <div className="ui-cmdk__empty">Ничего не найдено</div>
          ) : (
            results.map((item, i) => (
              <button
                key={item.path}
                type="button"
                data-active={i === active}
                className={cx("ui-cmdk__item", i === active && "ui-cmdk__item--active")}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(item)}
              >
                <span className="ui-cmdk__item-name">{item.name}</span>
                {item.group && <span className="ui-cmdk__item-group">{item.group}</span>}
              </button>
            ))
          )}
        </div>

        <div className="ui-cmdk__hint">
          <span>↑↓ — навигация</span>
          <span>Enter — открыть</span>
          <span>Esc — закрыть</span>
        </div>
      </div>
    </div>
  );
}
